import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { apiUrl } from "../config/config";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

/* =========================
   CHECKOUT FORM
========================= */
const CheckoutForm = ({ orderData, onSuccess, validateForm }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!stripe || !elements || loading) return;

    setLoading(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/api/stripe/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: orderData.total,
          currency: "usd",
        }),
      });

      const { client_secret } = await res.json();

      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${orderData.first_name} ${orderData.last_name}`,
            email: orderData.email,
          },
        },
      });

      if (result.error) {
        setLoading(false);
      } else if (result.paymentIntent.status === "succeeded") {
        onSuccess(result.paymentIntent.id);
      }
    } catch (err) {
      console.error("Stripe error:", err);
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handlePayment}
      className="relative mt-6 overflow-hidden rounded-[26px] border border-pastelPurple bg-white p-5 shadow-[0_14px_35px_rgba(47,34,84,0.08)]"
    >
      <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-keyLime/20 blur-3xl"></div>
      <div className="absolute -left-20 -bottom-20 h-56 w-56 rounded-full bg-pastelPurple/50 blur-3xl"></div>

      <div className="relative">
        <h3 className="text-xl font-extrabold leading-tight tracking-tight text-darkPurple">
          Pay with Card
        </h3>

        <div className="mt-3 flex items-center gap-3">
          <span className="h-1 w-14 rounded-full bg-keyLime"></span>
          <span className="h-1 w-7 rounded-full bg-pastelPurple"></span>
        </div>

        <div className="mt-5 rounded-2xl border border-pastelPurple bg-lightPastelPurple px-5 py-4 transition focus-within:border-themePurple focus-within:bg-white focus-within:ring-4 focus-within:ring-pastelPurple/60">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#211b33",
                  fontWeight: "500",
                  "::placeholder": {
                    color: "#5f5870",
                  },
                },
                invalid: {
                  color: "#ef4444",
                },
              },
            }}
          />
        </div>

        <button
          type="submit"
          disabled={!stripe || loading}
          className={`mt-5 flex w-full items-center justify-center gap-2 rounded-2xl px-7 py-3.5 text-center text-sm font-extrabold shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 ${
            loading || !stripe
              ? "cursor-not-allowed bg-gray-300 text-gray-600"
              : "bg-keyLime text-darkPurple hover:-translate-y-0.5 hover:bg-[#c8f531]"
          }`}
        >
          {loading && (
            <span className="h-5 w-5 rounded-full border-2 border-darkPurple border-t-transparent animate-spin"></span>
          )}
          {loading ? "Processing..." : "Pay Now"}
        </button>
      </div>
    </form>
  );
};

/* =========================
   STRIPE WRAPPER
========================= */
const StripeCheckout = ({ orderData, onSuccess, validateForm }) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm
      orderData={orderData}
      onSuccess={onSuccess}
      validateForm={validateForm}
    />
  </Elements>
);

export default StripeCheckout;
